import React, {useCallback, useState, useEffect, useRef} from 'react';
import {Card, Page, Layout, FormLayout, Spinner, Checkbox, TextField, Form, Frame, Badge, Button, Toast} from '@shopify/polaris';
import ReactHtmlParser from 'react-html-parser';
import { Editor } from "@tinymce/tinymce-react";
import createApp from '@shopify/app-bridge';
import {Redirect} from '@shopify/app-bridge/actions';
import {getSessionToken} from "@shopify/app-bridge-utils";

function DefaultContact() {
    const [subscription, setSubscription] = useState(null);
    const [replyEnabled, setReplyEnabled] = useState(false);
    const [replySubject, setReplySubject] = useState(null);
    const [replySenderName, setReplySenderName] = useState(null);
    const [replyToEmail, setReplyToEmail] = useState(null);
    const [replyCC, setReplyCC] = useState(null);
    const [replyMessage, setReplyMessage] = useState(null);
    const [pageJustLoadedReplySubject, setPageJustLoadedReplySubject] = useState(true);
    const [pageJustLoadedReplySenderName, setPageJustLoadedReplySenderName] = useState(true);
    const [pageJustLoadedReplyToEmail, setPageJustLoadedReplyToEmail] = useState(true);
    const [pageJustLoadedReplyMessage, setPageJustLoadedReplyMessage] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [submitErrorMessage, setSubmitErrorMessage] = useState('Error While Saving Settings');

    const [config, setConfig] = useState(null);

    useEffect(() => {
        fetchSubscription();
    }, []);

    const fetchSubscription = async () => {
        const query = new URLSearchParams(window.location.search);
        const configApp = {
            apiKey: window['apiKey'],
            host: query.get('host'),
            forceRedirect: true
        };
        setConfig(configApp);
        window['bridge-app'] = createApp(configApp);

        const sessionToken = await getSessionToken(window['bridge-app']);

        if (sessionToken) {
            const settings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionToken
                }
            };
            const response = await fetch('https://contactautoreply.com/api/subscription/current/' + window.$queryString, settings);
            // if user session expired reload the page
            if (response.status === 403) {
                window.location.reload();
                return false;
            }

            const responseJson = await response.json();

            if (response.status === 274) {
                window.location.href = 'https://contactautoreply.com/login/' + window.$queryString;
                return false;
            }
            if (response.status === 275) {
                const redirect = Redirect.create(window['bridge-app']);
                redirect.dispatch(Redirect.Action.REMOTE, responseJson.redirect_url);
                return false;
            }

            const subscription = responseJson;

            setSubscription(subscription);
            setReplyEnabled(subscription.reply_enabled);
            setReplySubject(subscription.reply_subject);
            setReplySenderName(subscription.reply_sender_name);
            setReplyToEmail(subscription.reply_to_email);
            setReplyCC(subscription.reply_cc);
            setReplyMessage(subscription.reply_message);

            let now = new Date();
            let trialEnd = new Date(subscription.trial_start);
            trialEnd.setDate(trialEnd.getDate() + 7);
            if (trialEnd <= now) {
                if (!subscription.is_premium) {
                    setReplyEnabled(false);
                }
            }
        }
    };

    const handleFormSubmit = () => {
        setPageJustLoadedReplySubject(false);
        setPageJustLoadedReplySenderName(false);
        setPageJustLoadedReplyToEmail(false);
        setPageJustLoadedReplyMessage(false);

        if (
            replySubject && replySenderName && replyToEmail && replyMessage
        ) {
            setSubmitLoading(true);

            subscription.reply_enabled = replyEnabled;
            subscription.reply_subject = replySubject;
            subscription.reply_sender_name = replySenderName;
            subscription.reply_to_email = replyToEmail;
            subscription.reply_cc = replyCC;
            subscription.reply_message = replyMessage;

            getSessionToken(window['bridge-app'])
                .then((sessionToken) => {
                    fetch(`https://contactautoreply.com/api/subscription/save/${window.$queryString}`, {
                        method: 'POST',
                        body: JSON.stringify(subscription),
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + sessionToken
                        }
                    }).then(response => {
                        if (response.status === 200) {
                            setSubmitSuccess(true);
                        } else if (response.status === 403) {
                            window.location.reload();
                            return false;
                        } else if (response.status === 405) {
                            setSubmitErrorMessage('<span>Your current subscription charge is not active so you are not able to activate the auto reply.</span>');
                            setSubmitError(true);
                            setReplyEnabled(false);
                        } else {
                            setSubmitError(true);
                        }
                        return response.json();
                    }).then(response => {
                        setSubmitLoading(false);
                    }).catch(err => {
                        setSubmitError(true);
                        setSubmitLoading(false);
                    });
                });
        }
    };

    const handleReplyEnabledChange = useCallback((value) => {
        setReplyEnabled(value);
    }, []);

    const handleReplySubjectChange = useCallback((value) => {
        setReplySubject(value);
    }, []);

    const handleReplySenderNameChange = useCallback((value) => {
        setReplySenderName(value);
    }, []);

    const handleReplyToEmailChange = useCallback((value) => {
        setReplyToEmail(value);
    }, []);

    const handleReplyCC = useCallback((value) => {
        setReplyCC(value);
    }, []);

    const handleReplyMessage = useCallback((value) => {
        setReplyMessage(value);
    }, []);

    const toggleSubmitSuccess = useCallback(() => {
        setSubmitSuccess(false);
    }, []);

    const toggleSubmitError = useCallback(() => {
        setSubmitError(false);
    }, []);

    return (
        <Page
            title="Built-in Contact Form"
            breadcrumbs={[{content: 'Dashboard', url: `/${window.$queryString}`}]}
        >
            <Layout>
                <Layout.Section>
                    {
                        subscription ?
                            [
                                <Form autoComplete={false} onSubmit={handleFormSubmit}>
                                    <Frame>
                                        <Card title="Built-in Contact Form Email Settings" sectioned>
                                            <FormLayout>
                                                <Checkbox
                                                    label="Automatic Reply Email Enabled?"
                                                    checked={replyEnabled}
                                                    onChange={handleReplyEnabledChange}
                                                />
                                                <TextField label="Subject" value={replySubject}
                                                    onChange={handleReplySubjectChange}
                                                    error={(replySubject || pageJustLoadedReplySubject) ? null : "Subject is required"}
                                                />
                                                <TextField label="Sender Name" value={replySenderName}
                                                    onChange={handleReplySenderNameChange}
                                                    error={(replySenderName || pageJustLoadedReplySenderName) ? null : "Sender Name is required"}
                                                />
                                                <TextField label="Reply To Email" type="email" value={replyToEmail}
                                                    onChange={handleReplyToEmailChange}
                                                    error={(replyToEmail || pageJustLoadedReplyToEmail) ? null : "Sender Email is required"}
                                                    helpText="If the client wants to reply to the reply email he gets, this is the email the reply will go to."
                                                />
                                                <TextField label="CC - Carbon Copy" value={replyCC}
                                                    onChange={handleReplyCC}
                                                    helpText="Provide a list of emails separated by ','"
                                                />
                                                <div className="Polaris-Label"
                                                    style={{
                                                       marginBottom: '-1rem',
                                                    }}
                                                >
                                                    <label className="Polaris-Label__Text">Message - HTML Format</label>
                                                </div>
                                                <Editor
                                                    apiKey="a6ggv9vsul6uq815qyoqpq02s1xf0cos3uj755vdpsc5hn16"
                                                    title="Message"
                                                    value={replyMessage}
                                                    init={{
                                                      height: 300,
                                                      menubar: false,
                                                      plugins: [
                                                        'advlist autolink lists link image',
                                                        'charmap print preview anchor help',
                                                        'searchreplace visualblocks code',
                                                        'insertdatetime media table paste wordcount'
                                                      ],
                                                      toolbar:
                                                        'code | undo redo | formatselect | bold italic | \
                                                        alignleft aligncenter alignright | \
                                                        bullist numlist outdent indent | help'
                                                    }}
                                                    onEditorChange={handleReplyMessage}
                                                />
                                                <div className="Polaris-Labelled__HelpText"
                                                    style={{
                                                       marginTop: '-1rem',
                                                    }}
                                                >
                                                    The Message Content needs to have email friendly format for it to
                                                    be properly rendered in the reply email and to make sure that it won't be flagged as spam.
                                                    <br/>
                                                    Available variables:
                                                    <br/>
                                                    {"{{client_name}}"} - name of the client who submitted the contact form
                                                    <br/>
                                                    {"{{client_email}}"} - email of the client who submitted the contact form
                                                </div>
                                            </FormLayout>
                                        </Card>
                                        <br/>
                                        {
                                            submitLoading ?
                                                <div className="main-button-wrapper">
                                                    <Button submit primary loading>Save</Button>
                                                </div>
                                                :
                                                <div className="main-button-wrapper">
                                                    <Button submit primary>Save</Button>
                                                </div>
                                        }
                                        {
                                            submitSuccess ?
                                                <Toast content="Settings Saved" onDismiss={toggleSubmitSuccess} />
                                                :
                                                null
                                        }
                                        {
                                            submitError ?
                                                <Toast content={ReactHtmlParser (submitErrorMessage)} error onDismiss={toggleSubmitError} />
                                                :
                                                null
                                        }
                                    </Frame>
                                </Form>
                            ]
                            :
                            <Card title="Contact Form AutoResponder Settings" sectioned>
                                <Spinner accessibilityLabel="Spinner example" size="large" color="teal" />
                            </Card>
                    }
                </Layout.Section>
            </Layout>
        </Page>
    );
}

export default DefaultContact;
